<template>
  <div>
    <h3 v-if="showHeader" class="print-color subtitle-border mb-2">{{ $t('report_preview.findings') }}：</h3>
    <div class="align-center highlight-area" v-if="showGlobalMap && (findings || spots)">
      <div align="center" class="pos-relative global-preview-map">
        <img src="/img/colon-preview-img.png" alt="" />

        <!-- EVAS2.0：直接給座標編號，不畫序號。
             病灶編號系統已移除（見 exam2/ShotInfoCard 的說明），總覽圖只負責
             「這些位置有東西」，是什麼東西由圖旁的清單交代。 -->
        <template v-if="spots">
          <div
            class="highlight-point highlight-point--plain"
            :class="`highlight-${index}`"
            v-for="index in spots"
            :key="'spot' + index"
          >
            <div class="highlight-inner-point"></div>
          </div>
        </template>

        <!-- 舊版：從 findings 的 observation 推位置，圈裡畫第幾個病灶 -->
        <template v-else>
          <div
            class="highlight-point"
            :class="`highlight-${getLocation(value)}`"
            v-for="(value, key) in findings"
            :key="'highlight'+value.id"
            v-if="getLocation(value) !== null"
          >
            <div class="highlight-inner-point">{{ key+1 }}</div>
          </div>
        </template>
      </div>
      <ol class="pl-3" v-if="showFindingNames && findings" >
        <li class="mb-2 font-weight-bold" v-for="(value, key) in findings" :key="key">
          {{ transI18n(value.name) }}
        </li>
      </ol>
    </div>
    <p v-else-if="showGlobalMap" class="pl-3">{{ $t('common.nil') }}</p>
    <hr v-if="showFindingDescriptions" class="finding-description-divider mb-2 mt-2" />
    <ul v-if="showFindingDescriptions" class="pl-3 mb-3" style="list-style: none;">
      <li v-for="(v, key) in findings" :key="v.id" class="mb-1">
        <FindingItemContent 
          :locationNumbers="getLocation(v, true)"
          :finding="v" 
          :index="key+1" 
          :images="getAllImages" 
          :imgArrange="imgArrange" 
          :showImages="showFindingImages"
          :showMap="showFindingMap"
          isColonReport
        />
      </li>
      <li v-if="showOtherImages" class="mb-1">
        <FindingOtherImages 
          :findings="findings" 
          :images="getAllImages" 
          :imgArrange="imgArrange" 
        />
      </li>
    </ul>
  </div>
</template>

<script>
import FindingItemContent from '@/components/commons/FindingItemContent.vue'
import { mapGetters, mapActions } from 'vuex'
import FindingOtherImages from '@/components/commons/FindingOtherImages.vue'
import { findMatchI18n } from '@/utils/findMatchI18n'
import { getLocationByLesionsObservation } from '@/utils/finding'
import { COLON_LOCATION_MAP } from '@/utils/previewLocationMap'
export default {
  name: 'FindingInfoByOrder',
  props: {
    findings: {
      type: Array,
      default: () => [],
    },
    imgArrange: {
      type: Number,
      default: 3
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    showGlobalMap: {
      type: Boolean,
      default: true
    },
    showFindingNames: {
      type: Boolean,
      default: true
    },
    showFindingDescriptions: {
      type: Boolean,
      default: true
    },
    showFindingImages: {
      type: Boolean,
      default: true
    },
    showFindingMap: {
      type: Boolean,
      default: true
    },
    showOtherImages: {
      type: Boolean,
      default: true
    },
    /**
     * EVAS2.0：要標示的座標編號陣列（例 `[5, 7, 12]`），由呼叫端從截圖的
     * landmarkID 算出來（見 utils/previewLocationMap）。
     *
     * 傳了就走這一條、且**不畫序號**；不傳（null）時完全走舊路徑，舊版報告
     * 頁行為 1:1 不變。刻意用另一個 prop 而不是把 findings 撐大：那個 prop
     * 同時餵給下面的 FindingItemContent，塞簡化物件進去會爆掉。
     */
    spots: {
      type: Array,
      default: null
    }
  },
  components: {
    FindingItemContent,
    FindingOtherImages
  },
  data() {
    return {
      // 表本身搬到 utils/previewLocationMap（Report2Preview 也要用同一份，
      // 三處各自維護必然分岔）。這裡保留同名欄位，getLocation 的呼叫不用改。
      locationMap: COLON_LOCATION_MAP,
    }
  },
  methods: {
    getLocation(finding, multiple = false) {
      return getLocationByLesionsObservation(finding, this.locationMap, multiple)
    },
    transI18n(text) {
      return findMatchI18n(text)
    }
  },
  computed: {
    ...mapGetters('examinations', ['getExaminationData']),
    getAllImages() {
      if (this.getExaminationData && this.getExaminationData.all) {
        return this.getExaminationData.all.sort((a, b) => b.id - a.id)
      }
      return []
    },
    
  },
}
</script>
<style lang="scss" scoped>
.finding-description-divider{
  border: 0;
  border-top: 1px dashed #B6BAC3;
}
.highlight-area{
  display: grid;
  grid-template-columns: auto;
}
@media print {
  .global-preview-map{
    display: inline-block;
    zoom: 0.5;
  }
  @supports not (zoom: 1) {
    .global-preview-map{
      transform: scale(0.5);
      transform-origin: top center;
    }
  }
}
.highlight-point{
  position: absolute;
  top: 0;
  left: 0;
  width: 34px;
  height: 34px;
  display: flex;
  border-radius: 50%;
  background-color: rgba(13, 148, 136, 0.3);
  align-items: center;
  justify-content: center;
  > .highlight-inner-point{
    width: 26px !important;
    height: 26px !important;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #ffffff;
    background-color: rgb(13, 148, 136)
  }
  /**575-522=53，所有的left 都要減53/2=26 */
  &.highlight-1{
    top:245px;
    left:180px;
  }
  &.highlight-2{
    top:245px;
    left:164px;
  }
  &.highlight-3{
    top:289px;
    left:202px;
  }
  &.highlight-4{
    top:289px;
    left:153px;
  }
  &.highlight-5{
    top:188px;
    left:132px;
  }
  &.highlight-6{
    top: 73px;
    left: 137px;
  }
  &.highlight-7{
    top: 114px;
    left: 252px;
  }
  &.highlight-8{
    top: 30px;
    left: 363px;
  }
  &.highlight-9{
    top: 173px;
    left: 363px;
  }
  &.highlight-10{
    top: 298px;
    left: 327px;
  }
  &.highlight-11{
    top: 266px;
    left: 357px;
  }
  &.highlight-12{
    top: 319px;
    left: 253px;
  }
  &.highlight-13{
    top: 398px;
    left: 253px;
  }
  &.highlight-14{
    top: 329px;
    left: 32px;
  }
  &.highlight-15{
    top: 348px;
    left: 32px;
  }
  &.highlight-16{
    top: 361px;
    left: 255px;
  }
}
</style>